import React, { useEffect, useState, useRef, useMemo, useImperativeHandle, forwardRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import TreeView from 'devextreme-react/tree-view';
import Calculator from '../../utils/calculator';
import AntCondition from '../../utils/AntCondition';
import AntLocalization from '../../utils/AntLocalization';
import AntAuth from '../../utils/AntAuth';
import { AntGlobal } from '../../utils/AntContext';
import './index.scss';
import { matchRoutes, useLocation } from 'react-router-dom';

export default function (props) {
    const location = useLocation();

    const appGlobal = AntGlobal();

    const treeView = useRef();
    const refContainer = useRef();

    const menuItems = useMemo(() => {
        let ar = props.items.map((i) => {
            if (i.items instanceof Array)
                i.items = i.items.map((j) => {
                    j.text = AntLocalization.translateText(j.text);
                    j.hint = j.text;
                    return j;
                });

            i.text = AntLocalization.translateText(AntCondition.getValue({}, i.text));
            i.hint = i.text;
            return i;
        });
        ar.push({
            name: 'exit',
            text: AntLocalization.translateText({
                vi: 'Đăng Xuất',
                en: 'Log Out',
                fr: 'Déconnexion',
                es: 'Cerrar sesión',
                id: 'Keluar',
                ja: 'ログアウト',
                zh: '登出',
                ko: '로그아웃',
                hi: 'लॉग आउट',
                ar: 'تسجيل الخروج'
            }),
            icon: 'runner'
        });
        return ar;
    }, [props.items]);

    const [bgImage] = React.useState(
        appGlobal.appInfo && appGlobal.appInfo.isAnte && appGlobal.appAssets && appGlobal.appAssets.images.searchPageBackground
            ? appGlobal.appAssets.images.searchPageBackground
            : props.backgroundImage
    );

    const onItemClick = (e) => {
        if (e?.itemData?.name === 'exit') {
            AntAuth.logOut();
            return;
        }
        expandByKey(e.node.key);
        if (props.onSelectionChanged) props.onSelectionChanged(e);
    };

    const expandByKey = (nodeKey) => {
        if (treeView.current) {
            var nodes = treeView.current.getNodes();

            var groupNode = nodes.find((n) => n.key === nodeKey || (n.children instanceof Array && n.children.some((c) => c.key == nodeKey)));
            if (groupNode) {
                for (let i = 0; i < nodes.length; i++) {
                    const node = nodes[i];

                    if (node.key !== groupNode.key && node.expanded) treeView.current.collapseItem(node.itemData);
                }
                treeView.current.scrollToItem(groupNode.key);
            }

            treeView.current.selectItem(nodeKey);
        }
    };

    const updateMenu = (autoSelect) => {
        if (treeView.current) {
            if (!props.menuOpened) treeView.current.collapseAll();
            else if (autoSelect) expandByKey(location.pathname);

            colorSelection();
        }
    };

    const colorSelection = () => {
        if (treeView.current) {
            let treeDOM = treeView.current.element();

            const nodeClass = 'dx-treeview-node';
            const selectedClass = 'dx-state-selected';
            const leafNodeClass = 'dx-treeview-node-is-leaf';

            const rootNodes = treeDOM.querySelectorAll(`.${nodeClass}:not(.${leafNodeClass})`);
            Array.from(rootNodes).forEach((node) => {
                node.classList.remove(selectedClass);
            });

            let selectedNode = treeDOM.querySelector(`.${nodeClass}.${selectedClass}`);
            while (selectedNode && selectedNode.parentElement) {
                if (selectedNode.classList.contains(nodeClass)) {
                    selectedNode.classList.add(selectedClass);
                }

                selectedNode = selectedNode.parentElement;
            }
        }
    };

    useEffect(() => {
        updateMenu(true);
    }, [treeView.current, location]);

    useEffect(() => {
        updateMenu();
    }, [props.menuOpened]);

    // ─── Tooltip cho menu items (khi collapsed, overflow:hidden che title) ───
    const [menuTip, setMenuTip] = useState(null);

    const onMenuMouseOver = useCallback((e) => {
        // Tìm treeview node chứa item text
        var node = e.target.closest('.dx-treeview-node');
        if (!node) { setMenuTip(null); return; }
        // Chỉ hiện khi menu collapsed (container width <= 50px)
        if (refContainer.current && refContainer.current.offsetWidth > 50) { setMenuTip(null); return; }
        // Lấy text từ title attribute hoặc từ text content
        var item = node.querySelector('.dx-treeview-item');
        var text = (item && item.getAttribute('title')) || '';
        if (!text) {
            var content = node.querySelector('.dx-item-content, .dx-treeview-item-content');
            text = content ? content.textContent.trim() : '';
        }
        if (!text) { setMenuTip(null); return; }
        var r = node.getBoundingClientRect();
        setMenuTip({ text: text, left: r.right + 6, top: r.top + r.height / 2 });
    }, []);

    const onMenuMouseOut = useCallback((e) => {
        var related = e.relatedTarget;
        if (related && related.closest && related.closest('.dx-treeview-node')) return;
        setMenuTip(null);
    }, []);

    const { className, children, onSelectionChanged, onMenuReady, items, ...rest } = props;

    return (
        <div className={`ant-navigation-menu dx-custom-treeview`} ref={refContainer} onMouseOver={onMenuMouseOver} onMouseOut={onMenuMouseOut}>
            {children}
            {menuTip && ReactDOM.createPortal(
                <div style={{ position: 'fixed', left: menuTip.left, top: menuTip.top, transform: 'translateY(-50%)', padding: '5px 10px', background: '#1a3561', color: '#fff', fontSize: '12px', whiteSpace: 'nowrap', borderRadius: '4px', zIndex: 99999, pointerEvents: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>{menuTip.text}</div>,
                document.body
            )}
            <div className={'ant-menu-container'}>
                <TreeView
                    expandEvent={'click'}
                    width={'100%'}
                    {...rest}
                    items={menuItems}
                    onInitialized={(event) => {
                        treeView.current = event.component;
                        event.component.option('deferRendering', false);
                    }}
                    onItemClick={onItemClick}
                    onContentReady={(e) => {
                        if (props.onMenuReady) props.onMenuReady(e);
                        updateMenu(true);
                    }}
                    onSelectionChanged={(e) => {
                        updateMenu();
                    }}
                    selectByClick
                    selectionMode={'single'}
                    keyExpr={'path'}
                    // scrollDirection="horizontal"
                />

                {/* <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10, marginBottom: 20 }}>
                        <img style={{ width: 150, opacity: 0.7 }} src={bgImage}></img>
                    </div> */}
            </div>
        </div>
    );
}
